import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, emptyCosts, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import ClientFields from "../components/ClientFields";
import CostTable from "../components/CostTable";
import SummaryCard from "../components/SummaryCard";
import NotesCard from "../components/NotesCard";
import { Trash2, Save, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";

export default function QuoteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [values, setValues] = useState({ name: "", client_name: "", client_phone: "" });
  const [costs, setCosts] = useState(emptyCosts());
  const [customItems, setCustomItems] = useState([]);
  const [salePrice, setSalePrice] = useState(0);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const skipDirty = useRef(true);

  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => {
      const q = r.data;
      setQuote(q);
      setValues({
        name: q.name,
        client_name: q.client_name || "",
        client_phone: q.client_phone || "",
      });
      setCosts({ ...emptyCosts(), ...(q.costs || {}) });
      setCustomItems(q.custom_items || []);
      setSalePrice(q.sale_price || 0);
      setNotes(q.notes || "");
      setLoading(false);
      skipDirty.current = true;
    });
  }, [id]);

  useEffect(() => {
    if (skipDirty.current) {
      skipDirty.current = false;
      return;
    }
    setDirty(true);
  }, [values, costs, customItems, salePrice, notes]);

  const totalCosts =
    Object.values(costs).reduce((s, v) => s + Number(v || 0), 0) +
    customItems.reduce((s, it) => s + Number(it?.amount || 0), 0);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const r = await api.put(`/quotes/${id}`, {
        ...values,
        costs,
        custom_items: customItems,
        sale_price: Number(salePrice || 0),
        notes,
      });
      setQuote(r.data);
      setDirty(false);
      toast.success("Zapisano wycenę");
    } catch {
      toast.error("Błąd zapisu");
    } finally {
      setSaving(false);
    }
  }, [id, values, costs, customItems, salePrice, notes]);

  const remove = async () => {
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Wycena usunięta");
      nav("/wyceny");
    } catch {
      toast.error("Nie udało się usunąć");
    }
  };

  const convert = async () => {
    if (dirty) await save();
    try {
      const r = await api.post(`/quotes/${id}/convert`);
      toast.success("Utworzono zlecenie z wyceny");
      nav(`/zlecenia/${r.data.id}`);
    } catch {
      toast.error("Błąd konwersji");
    }
  };

  if (loading) return <div className="surface p-10 text-center" data-testid="quote-loading">Wczytywanie...</div>;
  if (!quote) return <div className="surface p-10 text-center">Nie znaleziono wyceny</div>;

  return (
    <div className="space-y-6" data-testid="quote-detail-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2">
          Wycena
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-semibold tracking-tight">
          {quote.name}
        </h1>
        <div className="text-xs text-[var(--text-muted)] mt-2">
          Utworzono {formatDate(quote.created_at)}
        </div>
      </header>

      <ClientFields values={values} onChange={setValues} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CostTable
            costs={costs}
            customItems={customItems}
            onChange={setCosts}
            onChangeCustomItems={setCustomItems}
            title="Koszty wyceny"
          />
        </div>
        <div>
          <SummaryCard
            salePrice={salePrice}
            totalCosts={totalCosts}
            onChangeSalePrice={setSalePrice}
          />
        </div>
      </div>

      <NotesCard value={notes} onChange={setNotes} />

      <div className="surface p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-[var(--text-muted)]">
          {dirty ? "Masz niezapisane zmiany" : "Wszystkie zmiany zapisane"}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={!dirty || saving}
            onClick={save}
            data-testid="save-quote-btn"
          >
            <Save size={16} className="mr-2" />
            {saving ? "Zapisywanie..." : "Zapisz"}
          </Button>
          <Button
            onClick={convert}
            data-testid="convert-quote-btn"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            <ArrowRightCircle size={16} className="mr-2" />
            Utwórz zlecenie
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white"
                data-testid="delete-quote-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Usuń
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Usunąć wycenę?</AlertDialogTitle>
                <AlertDialogDescription>
                  Tej operacji nie można cofnąć.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-quote-cancel">Anuluj</AlertDialogCancel>
                <AlertDialogAction
                  onClick={remove}
                  data-testid="delete-quote-confirm"
                  className="bg-[var(--danger)] hover:bg-[var(--danger)]/90"
                >
                  Usuń
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
